import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { combineLatest, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { filter, finalize, switchMap, take } from 'rxjs/operators';
import { fadeAnimation, showAnimation } from 'src/animations';
import { CreateContractDialogComponent, CreateContractDialogComponentInputData } from 'src/app/lib/contract/components/dialog/create-contract-dialog/create-contract-dialog.component';
import { CONTRACTS_PROVIDER_SERVICE } from 'src/app/lib/contract/interfaces';
import { Address } from 'src/app/lib/location/classes';
import { CreateOfferComponent, CreateOfferComponentInputData } from 'src/app/lib/offer/components/dialog/create-offer/create-offer.component';
import { SidenavOption } from 'src/app/lib/shared/classes';
import { CompleteRequestDialogComponent, CompleteRequestDialogComponentInputData } from 'src/app/lib/shared/components/dialog/complete-request-dialog/complete-request-dialog.component';
import { DoubleCheckDialogComponent, DoubleCheckDialogComponentInputData } from 'src/app/lib/shared/components/dialog/double-check-dialog/double-check-dialog.component';
import { HereMapDialogComponent, HereMapDialogComponentData } from 'src/app/lib/shared/components/dialog/here-map-dialog/here-map-dialog.component';
import { CONCURRENCY_PROVIDER_SERVICE, ISSUES_PROVIDER_SERVICE, OFFERS_PROVIDER_SERVICE } from 'src/app/lib/shared/interfaces';
import { SessionService } from 'src/app/lib/shared/services/session.service';
import { SidenavService } from 'src/app/lib/shared/services/sidenav.service';
import { Contact, Customer } from '../../../classes';
import { CONTACTS_PROVIDER_SERVICE, CUSTOMER_PROVIDER_SERVICE } from '../../../interfaces';
import { CustomerService } from '../../../services/customer.service';
import { CreateContactDialogComponent } from '../../dialog/create-contact-dialog/create-contact-dialog.component';
import { DeleteCustomerRequestDialogComponent } from '../../dialog/delete-customer-request-dialog/delete-customer-request-dialog.component';
import { CustomerDetailViewComponentService } from './customer-detail-view-component-service';

@Component({
  selector: 'app-customer-detail-view',
  templateUrl: './customer-detail-view.component.html',
  styleUrls: ['./customer-detail-view.component.css'],
  providers: [
    { provide: CUSTOMER_PROVIDER_SERVICE, useClass: CustomerDetailViewComponentService },
    { provide: CONCURRENCY_PROVIDER_SERVICE, useExisting: CUSTOMER_PROVIDER_SERVICE },
    { provide: ISSUES_PROVIDER_SERVICE, useExisting: CUSTOMER_PROVIDER_SERVICE },
    { provide: CONTACTS_PROVIDER_SERVICE, useExisting: CUSTOMER_PROVIDER_SERVICE },
    { provide: CONTRACTS_PROVIDER_SERVICE, useExisting: CUSTOMER_PROVIDER_SERVICE },
    { provide: OFFERS_PROVIDER_SERVICE, useExisting: CUSTOMER_PROVIDER_SERVICE },
    SidenavService
  ],
  animations: [showAnimation, fadeAnimation]
})
export class CustomerDetailViewComponent implements OnDestroy, OnInit {

  appearance: 'fill' | 'outline' | 'standard' = 'fill';

  disabled: boolean = false;

  reset: boolean = false;

  formGroup: FormGroup;

  sidenavOptions = {
    'editMode': {
      'icon': () => this.disabled ? 'edit' : 'edit_off',
      'title': () => this.disabled ? 'Bearbeiten' : 'Sperren',
      'onClick': () => of(this.toggleEditMode()),
      'visible': () => true,
      'appearance': () => 'default'
    } as SidenavOption,
    'save': {
      'icon': () => this.formGroup.dirty ? 'save' : 'done',
      'title': () => this.formGroup.dirty ? 'Speichern' : 'Aktuell',
      'onClick': () => this.formGroup.dirty ? this.saveChanges() : of(null),
      'visible': () => !this.disabled,
      'appearance': () => this.formGroup.dirty ? 'success' : 'ok'
    } as SidenavOption,
    'activate': {
      'icon': () => 'upcoming',
      'title': () => 'Aktiver Kunde',
      'onClick': () => this.activateCustomer(),
      'visible': () => !((this.formGroup.controls['customer'] as FormGroup).controls['customeractive'].value),
      'appearance': () => 'ok'
    } as SidenavOption,
    'discard': {
      'icon': () => 'clear',
      'title': () => 'Verwerfen',
      'onClick': () => this.discardChanges(),
      'visible': () => !this.disabled && this.formGroup.dirty,
      'appearance': () => 'danger'
    } as SidenavOption,
    'watched_status': {
      'icon': () => this.formGroup.controls['isWatched'].value ? 'visibility' : 'visibility_off',
      'title': () => this.formGroup.controls['isWatched'].value ? 'Beobachtet' : 'Nicht beob.',
      'onClick': () => this.toggleWatchedStatus(),
      'visible': () => true,
      'appearance': () => 'dark'
    } as SidenavOption,
    'delete': {
      'icon': () => 'delete_forever',
      'title': () => 'Löschen',
      'onClick': () => this.deleteCustomer(),
      'visible': () => !this.disabled && this.rightsMap.delete_customers,
      'appearance': () => 'danger'
    } as SidenavOption
  };

  rightsMap = {
    "delete_customers": false
  };

  private _subscriptions: Subscription[] = [];

  constructor(
    @Inject(CUSTOMER_PROVIDER_SERVICE) public customerDetailViewComponentService: CustomerDetailViewComponentService,
    private _dialog: MatDialog,
    private _formBuilder: FormBuilder,
    private _sidenavService: SidenavService,
    private _snackBar: MatSnackBar,
    private _sessionService: SessionService,
    private _customerService: CustomerService
  ) { }

  activateCustomer() {
    this._dialog.open(DoubleCheckDialogComponent, {
      panelClass: 'no-padding-dialog-container',
      data: {
        'doubleCheckValue': (this.formGroup.controls['customer'] as FormGroup).controls['customernumber'].value,
        'icon': 'upcoming',
        'mode': 'numeric',
        'text': `Möchten Sie den Kunden wirklich wieder aktivieren? Bitte geben Sie die Kundennummer ${(this.formGroup.controls['customer'] as FormGroup).controls['customernumber'].value} ein.`,
        'title': 'Kunden reaktivieren'
      } as DoubleCheckDialogComponentInputData
    }).afterClosed().pipe(
      filter(x => x ? true : false),
      switchMap(() => {
        (this.formGroup.controls['customer'] as FormGroup).controls['customeractive'].setValue(true);
        return this.saveChanges();
      }),
    ).subscribe(() => {
      if (this.disabled) this.toggleEditMode();
    });
    return of(null);
  }

  addContact() {
    this._dialog.open(CreateContactDialogComponent, {
      panelClass: "no-padding-dialog-container",
      autoFocus: false,
      data: {
        preventNavigation: true
      }
    }).afterClosed().subscribe((contact: Contact) => {
      if (contact) this.customerDetailViewComponentService.customerId$
        .pipe(take(1), switchMap((customerId: number) => this._customerService.linkContact(customerId, contact.contactpersid)))
        .subscribe({
          next: () => this.customerDetailViewComponentService.refreshCurrentCustomerContacts(),
          error: () => this._snackBar.open('Es ist ein Fehler aufgetreten', 'Ok', { duration: 2000 })
        });
    });
  }

  addContract() {
    this.customerDetailViewComponentService.customerId$.pipe(take(1)).subscribe((customerId: number) => {
      this._dialog.open(CreateContractDialogComponent, {
        panelClass: "no-padding-dialog-container",
        autoFocus: false,
        data: {
          preventNavigation: true,
          patchValue: { 'contractcustomer': customerId }
        } as CreateContractDialogComponentInputData
      }).afterClosed().subscribe((contract) => {
        if (contract) this.customerDetailViewComponentService.refreshCurrentCustomerContracts();
      });
    });
  }

  addOffer() {
    this.customerDetailViewComponentService.customerId$.pipe(take(1)).subscribe((customerId: number) => {
      this._dialog.open(CreateOfferComponent, {
        panelClass: 'no-padding-dialog-container',
        data: {
          preventNavigation: true,
          patchValue: { offercustomer: customerId }
        } as CreateOfferComponentInputData
      }).afterClosed().subscribe((result) => {
        if (result) this.customerDetailViewComponentService.refreshCurrentCustomerOffers();
      });
    })
  }

  afterStoring() {
    this.formGroup.markAsUntouched();
    this.formGroup.markAsPristine();
    this.customerDetailViewComponentService.refreshCurrentCustomer();
    this._snackBar.open('Änderungen erfolgreich gespeichert!', 'Speichern', {
      duration: 2000
    });
  }

  clearName = () => {
    this.nameControl.setValue(null);
    this.nameControl.markAsDirty();
  }

  deleteCustomer() {
    let subject = new Subject();
    this.customerDetailViewComponentService.customer$.pipe(take(1)).subscribe((customer: Customer) => {
      var number = customer.customernumber ? customer.customernumber.trim() : Math.floor(1000 + Math.random() * 9000).toFixed(0);
      this._dialog.open(DoubleCheckDialogComponent, {
        autoFocus: false,
        panelClass: 'no-padding-dialog-container',
        data: {
          mode: 'numeric',
          title: 'Löschen bestätigen',
          text: `Bitte geben Sie die Nummer ${number} ein, um den Kunden dauerhaft zu löschen`,
          doubleCheckValue: number
        } as DoubleCheckDialogComponentInputData
      }).afterClosed().pipe(
        filter(x => {
          if (x) return true;
          else {
            subject.complete();
            return false;
          }
        }),
        switchMap(() => this._dialog.open(DeleteCustomerRequestDialogComponent, {
          autoFocus: false,
          panelClass: 'no-padding-dialog-container'
        }).afterClosed())
      ).subscribe((result) => {
        if (result) {
          this.customerDetailViewComponentService.deleteCurrentCustomer(result).subscribe(subject);
        } else subject.complete();
      });
    });
    return subject.asObservable();
  }

  discardChanges(): Observable<Object> {
    this.reset = true;
    return this.customerDetailViewComponentService.refreshCurrentCustomer();
  }

  initRights = (soft: boolean = true) => {
    for (let index in this.rightsMap) this._sessionService.hasRight(index, soft).subscribe(result => this.rightsMap[index] = result);
  }

  ngOnDestroy(): void {
    for (let sub of this._subscriptions) {
      sub.unsubscribe();
      this._subscriptions = [];
    }
    this.customerDetailViewComponentService.dispose();
  }

  ngOnInit(): void {

    this.formGroup = this._formBuilder.group({
      customer: this._formBuilder.group(new Customer()),
      address: this._formBuilder.group(new Address()),
      isWatched: this._formBuilder.control(false)
    });

    this.setValidators();

    this._sidenavService.setOptions(Object.values(this.sidenavOptions));

    this._subscriptions.push(...[
      this.customerDetailViewComponentService.customer$.pipe(filter(x => this.formGroup.pristine || this.reset)).subscribe((customer: Customer) => this.setCustomer(customer)),
      this.customerDetailViewComponentService.customer$.pipe(filter((x: Customer) => x && !this.disabled && !x.customeractive)).subscribe(() => {
        this.toggleEditMode();
      })
    ]);

    this.initRights();
  }

  saveChanges() {
    let subject = new Subject();

    combineLatest([
      this._dialog.open(CompleteRequestDialogComponent, {
        panelClass: 'no-padding-dialog-container',
        data: {
          addNote: true
        } as CompleteRequestDialogComponentInputData
      }).afterClosed(),
      this.customerDetailViewComponentService.customer$
    ])
      .pipe(take(1))
      .subscribe(([output, customer]) => {
        if (output.abort) {
          subject.complete();
          return;
        }

        this.customerDetailViewComponentService
          .saveChanges(
            (this.formGroup.controls['customer'] as FormGroup).getRawValue(),
            output.note,
            (this.formGroup.controls['address'] as FormGroup).getRawValue()
          )
          .pipe(finalize(() => this.afterStoring()))
          .subscribe(subject);
      });

    return subject.asObservable();
  }

  setCustomer(customer: Customer) {
    this.reset = false;
    this.formGroup.controls['customer'].patchValue(customer);
    this.formGroup.controls['address'].patchValue(customer.address ? customer.address : new Address());
    this.formGroup.markAsPristine();
    timer(100).pipe(switchMap(() => this._customerService.customerIsWatched(customer.customerid))).subscribe((result: boolean) => this.formGroup.controls['isWatched'].setValue(result));
    this._customerService.saveHistory(customer.customerid);
  }

  setValidators() {
    (this.formGroup.controls['customer'] as FormGroup).controls['customernumber'].disable();
  }

  showAddress = () => this._dialog.open(HereMapDialogComponent, {
    panelClass: 'no-padding-dialog-container',
    data: { address: this.formGroup.controls['address'].value } as HereMapDialogComponentData
  });

  showCustomerHistory() {

  }

  toggleEditMode() {
    let pristine = this.formGroup.pristine;
    this.formGroup[this.disabled ? 'enable' : 'disable']({ emitEvent: false });
    this.disabled = !this.disabled;
    if (pristine) this.formGroup.markAsPristine();
  }

  toggleWatchedStatus() {
    let value = this.formGroup.controls['isWatched'].value ? true : false;
    this.customerDetailViewComponentService.customerId$.pipe(switchMap((customerId: number) => this._customerService.setCustomerWatchedStatus(customerId, !value)))
      .subscribe(() => this.formGroup.controls['isWatched'].setValue(!value));

    return of(null);
  }

  toggleIssueTypeVisibility = (event: MouseEvent, issueType: string) => {
    if (event) event.stopPropagation();
    this.customerDetailViewComponentService.visibleIssueTypes$.pipe(take(1)).subscribe((visibleTypes) => {
      visibleTypes[issueType] = !visibleTypes[issueType];
      this.customerDetailViewComponentService.setVisibleIssueTypes(visibleTypes);
    });
  }

  @HostListener('keydown', ['$event'])
  private _keydown(event: KeyboardEvent) {
    if (event.ctrlKey && event.key === 's') {
      event.preventDefault();
      event.stopPropagation();
      if (this.formGroup.dirty) this.saveChanges();
      else this._snackBar.open('Keine Änderungen zum Speichern erkannt', 'Ok', {
        duration: 2000
      });
    }
  }

  get addressControl(): AbstractControl {
    return this.formGroup.controls['address'];
  }

  get nameControl(): AbstractControl {
    return (this.formGroup.controls['customer'] as FormGroup).controls['customername'] as AbstractControl;
  }
  get numberControl(): AbstractControl {
    return (this.formGroup.controls['customer'] as FormGroup).controls['customernumber'] as AbstractControl;
  }

}
